import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GateVideo from '../components/progress/GateVideo'
import { useGateScreenData, useSubmitGateProof } from '../hooks/gate/useGateScreenData'
import { ScreenPath } from '../navigation'
import AppShell from '../ui/layout/AppShell'
import Button from '../ui/primitives/Button'
import AppIcon from '../ui/icons/AppIcon'
import GateChallengeCard from '../ui/gate/GateChallengeCard'
import GateCompletion from '../ui/gate/GateCompletion'
import GateHeader from '../ui/gate/GateHeader'
import GateSubmissionGrid from '../ui/gate/GateSubmissionGrid'
import GateSubmittedBanner from '../ui/gate/GateSubmittedBanner'
import GateSubmitOverlay from '../ui/gate/GateSubmitOverlay'
import styles from '../ui/gate/GateScreen.module.css'

interface GateScreenProps {
  gateId: string
}

export default function GateScreen({ gateId }: GateScreenProps) {
  const navigate = useNavigate()
  const { data, isLoading } = useGateScreenData(gateId)
  const { mutateAsync: submitProof, isPending } = useSubmitGateProof(gateId)
  const [showSubmit, setShowSubmit] = useState(false)
  const [replayNonce, setReplayNonce] = useState(0)

  if (isLoading || !data) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>Loading…</p>
      </AppShell>
    )
  }

  return (
    <>
      <AppShell>
        <div className={styles.nav}>
          <button
            type="button"
            className={styles.back}
            onClick={() => navigate(ScreenPath.home)}
            title="Back"
          >
            <AppIcon icon="mdi:chevron-left" size={18} />
          </button>
        </div>

        <div>
          <GateHeader city={data.city} />
          <GateChallengeCard
            title={data.challengeTitle}
            description={data.challengeDescription}
            imageUrl={data.imageUrl}
            showReplay={Boolean(data.videoUrl && data.userHasSeenVideo)}
            onReplay={() => setReplayNonce((nonce) => nonce + 1)}
          />
          <GateCompletion
            members={data.members}
            submittedCount={data.submittedCount}
            teamSize={data.teamSize}
          />

          {!data.userHasSubmitted && (
            <div className={styles.submitBtn}>
              <Button variant="blue" onClick={() => setShowSubmit(true)}>
                Submit your proof
              </Button>
            </div>
          )}

          {data.userHasSubmitted && !data.isCleared && (
            <GateSubmittedBanner waitingCount={data.waitingCount} />
          )}

          <GateSubmissionGrid submissions={data.submissions} />
        </div>
      </AppShell>
      <GateVideo gateId={gateId} replayNonce={replayNonce} />

      {showSubmit && (
        <GateSubmitOverlay
          onClose={() => setShowSubmit(false)}
          onSubmit={async (file) => {
            await submitProof(file)
          }}
          isSubmitting={isPending}
        />
      )}
    </>
  )
}
